import {
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query";
import { Mutex } from "async-mutex";

import { ENV_CONFIG } from "../../config/global.config";
import type { RootState } from "../app.store";
import { defaultHeaders } from "../app.utils";
import { userLoggedOut, verifyUser } from "../features/auth/auth.slice";
import type { IAuthResponse, IRefreshDto } from "../features/auth/auth.types";

// Create a new mutex
const mutex = new Mutex();

export const baseQuery = fetchBaseQuery({
  baseUrl: ENV_CONFIG.AUTH_API_URL, // Default to Auth URL, but services can override if they wrap this or pass a different one.
  // Wait, different services have different baseURLs.
  // We need to allow passing baseUrl or creating specific base queries.
  // The services currently create their own fetchBaseQuery.
  // We should create a factory or just a re-usable baseQuery wrapped function.
});
// Actually, looking at the existing code, each service creates its own fetchBaseQuery with specific baseUrl.
// So we should make a factory function that takes the baseUrl/config.

export const createBaseQuery = (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState)?.auth?.accessToken;
      return defaultHeaders(headers, token);
    },
  });

export const baseQueryWithReauth: (
  baseUrl: string
) => BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = (baseUrl) => async (args, api, extraOptions) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();

  const baseQuery = createBaseQuery(baseUrl);
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const state = api.getState() as RootState;
        const refreshToken = state.auth.refreshToken;

        if (refreshToken) {
          // try to refresh the token
          const refreshResult = await createBaseQuery(ENV_CONFIG.AUTH_API_URL)(
            {
              url: "/auth/refresh",
              method: "POST",
              body: { refreshToken } as IRefreshDto,
            },
            api,
            extraOptions
          );

          if (refreshResult.data) {
            const data = refreshResult.data as IAuthResponse;
            // store the new token
            api.dispatch(
              verifyUser({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
              })
            );

            // retry the initial query
            result = await baseQuery(args, api, extraOptions);
          } else {
            api.dispatch(userLoggedOut());
          }
        } else {
          api.dispatch(userLoggedOut());
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }
  return result;
};
